import React, { useState, useEffect } from "react";
import Swal from "sweetalert2";
import forge from "node-forge";
import { fetchDebtSummary, fetchScanDetails } from "../services/tamisemiService";
import "./custom.css";

const API_KEY = process.env.REACT_APP_API_KEY;

// Temporary switch: when false only TPF offences are shown and TAMISEMI is not called.
const SHOW_TAMISEMI_PARKING = false;

function decryptPayload(payload) {
  try {
    let raw = payload;
    try {
      const firstDecode = atob(payload);
      if (/^[A-Za-z0-9+/]+=*$/.test(firstDecode.trim()) && firstDecode.length % 4 === 0) {
        console.warn("Double base64 detected — unwrapping one layer");
        raw = firstDecode;
      }
    } catch (_) {
    }

    const key = API_KEY.substring(0, 32).padEnd(32, "\0");
    const md = forge.md.sha256.create();
    md.update(API_KEY, "raw"); // raw = treat as binary string (matches PHP default)
    const sha256hex = md.digest().toHex();
    const iv = sha256hex.substring(0, 16); // 16 ASCII characters → 16-byte IV
    const encryptedBytes = forge.util.decode64(raw);
    const decipher = forge.cipher.createDecipher("AES-CBC", key);
    decipher.start({ iv });
    decipher.update(forge.util.createBuffer(encryptedBytes, "raw"));
    const success = decipher.finish();

    if (!success) {
      console.error("Decryption padding check failed — key/IV mismatch");
      return null;
    }

    const decryptedText = decipher.output.toString("utf8");
    return JSON.parse(decryptedText);
  } catch (err) {
    console.error("Decryption error:", err);
    return null;
  }
}

const formatScanDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (isNaN(d.getTime())) return String(value);
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
};


// dd/mm/yyyy; guards null / "0000-00-00" / epoch-fallback dates
const formatDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (isNaN(d.getTime()) || d.getFullYear() < 1971) return "—";
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
};

const formatMoney = (n) =>
  Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// tmsCheck returns a plain list of offences (or {status: "...HAIDAIWI."} when
// there is no debt); reshape it into the structure the results view expects.
function normalizeTmsResponse(json) {
  const list = Array.isArray(json) ? json : [];
  const pending = list.filter((t) => String(t.status).toUpperCase() === "PENDING");
  return {
    status: "success",
    pending_transactions: pending,
    totalPendingAmount: pending.reduce((sum, t) => sum + Number(t.charge || 0) + Number(t.penalty || 0), 0),
  };
}

function usePagination(items) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const pages = Math.max(1, Math.ceil(items.length / pageSize));
  const current = Math.min(page, pages);
  const start = (current - 1) * pageSize;
  return {
    start,
    rows: items.slice(start, start + pageSize),
    bar: (
      <div className="bill-pagination">
        <span className="bill-page-of">{current} of {pages}</span>
        <button disabled={current === 1} onClick={() => setPage(1)} aria-label="First page">«</button>
        <button disabled={current === 1} onClick={() => setPage(current - 1)} aria-label="Previous page">‹</button>
        <span className="bill-page-current">{current}</span>
        <button disabled={current === pages} onClick={() => setPage(current + 1)} aria-label="Next page">›</button>
        <button disabled={current === pages} onClick={() => setPage(pages)} aria-label="Last page">»</button>
        <select
          value={pageSize}
          onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
          aria-label="Rows per page"
        >
          {[5, 10, 20, 50].map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
      </div>
    ),
  };
}

function OffenceTable({ items, searchType }) {
  const rows = items;
  const start = 0;
  const columns = {
    vehicle: ["S/N", "Reference", "Location", "Offence", "Charge", "Penalty", "Status", "Issued Date"],
    license: ["S/N", "Reference", "Vehicle", "Location", "Offence", "Charge", "Penalty", "Status", "Issued Date"],
    reference: ["S/N", "Vehicle", "Location", "Offence", "Charge", "Penalty", "Status", "Issued Date"],
  }[searchType] || [];
  const fields = (t) => ({
    vehicle: [t.reference, t.location, t.offence, t.charge, t.penalty, t.status, t.issued_date],
    license: [t.reference, t.vehicle, t.location, t.offence, t.charge, t.penalty, t.status, t.issued_date],
    reference: [t.vehicle, t.location, t.offence, t.charge, t.penalty, t.status, t.issued_date],
  }[searchType]);

  const align = (label) =>
    label === "S/N" || label === "Status" ? "center" : ["Charge", "Penalty"].includes(label) ? "right" : "left";

  const cell = (label, value) => {
    if (label === "Status") {
      const st = (value || "").toLowerCase();
      return <span className={`bill-status bill-status-${st}`}>{st.toUpperCase()}</span>;
    }
    if (label === "Issued Date") return formatDate(value);
    if (["Charge", "Penalty"].includes(label)) return formatMoney(value);
    return value ?? "—";
  };

  return (
    <>
      <div className="bill-table-wrapper">
        <table className="bill-table">
          <thead>
            <tr>
              {columns.map((c) => <th key={c} className={align(c)}>{c}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((t, i) => (
              <tr key={start + i}>
                {[start + i + 1, ...fields(t)].map((v, j) => (
                  <td key={j} className={`${align(columns[j])} ${["S/N", "Charge", "Penalty"].includes(columns[j]) ? "strong" : ""}`}>
                    {j === 0 ? v : cell(columns[j], v)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function Channel1ScanTable({ scans }) {
  const { rows, start, bar } = usePagination(scans);

  return (
    <>
      <div className="bill-table-wrapper">
        <table className="bill-table tamisemi-scan-table">
          <thead>
            <tr>
              <th className="center">No.</th>
              <th className="left">Date</th>
              <th className="left">Council</th>
              <th className="right">Amount (TZS)</th>
              <th className="left">Payment Plan</th>
              <th className="center">status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((item, idx) => {
              const plan = item.paymentPlan === "HOUR" ? "Hourly" : (item.paymentPlan || "Hourly");
              const status = item.status || "Active";
              return (
                <tr key={item.id || start + idx}>
                  <td className="center strong">{start + idx + 1}</td>
                  <td className="left">{formatScanDate(item.startTime || item.date || item.createdAt)}</td>
                  <td className="left">{item.councilName || item.council || "—"}</td>
                  <td className="right strong">{formatMoney(item.payableAmount ?? item.amount ?? 0)}</td>
                  <td className="left">{plan}</td>
                  <td className="center">
                    <span className="status-pill-active">{status}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {scans.length > 10 && bar}
    </>
  );
}

function Channel2ScanTable({ scans }) {
  // Sort oldest to newest to match previous bills chronological scan order
  const sortedScans = React.useMemo(() => {
    return [...scans].sort((a, b) => {
      const timeA = new Date(a.startTime || 0).getTime();
      const timeB = new Date(b.startTime || 0).getTime();
      if (timeA !== timeB) return timeA - timeB;
      return String(a.id || "").localeCompare(String(b.id || ""));
    });
  }, [scans]);

  const { rows, start, bar } = usePagination(sortedScans);

  return (
    <>
      <div className="bill-table-wrapper">
        <table className="bill-table tamisemi-scan-table">
          <thead>
            <tr>
              <th className="center">SN.</th>
              <th className="left">Amount (TZS)</th>
              <th className="left">Parking Date</th>
              <th className="left">Parking Time & Location</th>
              <th className="right">Duration</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((item, idx) => (
              <tr key={item.id || start + idx}>
                <td className="center strong">{start + idx + 1}</td>
                <td className="left strong">{formatMoney(item.payableAmount ?? item.amount ?? 0)}</td>
                <td className="left">{formatScanDate(item.startTime || item.parkingDate || item.date)}</td>
                <td className="left">{item.paymentPlan || item.parkingTime || item.details || "—"}</td>
                <td className="right">{item.councilName || item.duration || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {sortedScans.length > 10 && bar}
    </>
  );
}

function BillCard({ bill, isPrevious, searchQuery }) {
  const [open, setOpen] = useState(false);
  const [scans, setScans] = useState(bill.transactions?.length ? bill.transactions : null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const controlNo = bill.control_number || bill.controlNumber || "—";
  const channelId = bill.channel_id ? String(bill.channel_id) : (isPrevious ? "2" : "1");
  const plateNumber = bill.plate_number || searchQuery;

  const loadScans = async () => {
    if (!plateNumber) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetchScanDetails(plateNumber, channelId, 0, 1000);
      const items =
        res?.data?.itemList ||
        res?.data?.scans ||
        res?.data?.content ||
        res?.data?.summary ||
        res?.itemList ||
        res?.scans ||
        res?.content ||
        (Array.isArray(res?.data) ? res.data : []) ||
        (Array.isArray(res) ? res : []);
      setScans(items);
    } catch (err) {
      console.error("Failed to fetch scan details:", err);
      setError("Failed to fetch scan details. Please check your connection and try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = () => {
    const nextOpen = !open;
    setOpen(nextOpen);
    if (nextOpen && scans === null) {
      loadScans();
    }
  };

  return (
    <div className="bill-block">
      <div className="bill-card">
        {isPrevious && (
          <p className="parking-note">
            <strong>Previous Bills [ TERMIS ]</strong>
          </p>
        )}
        <div className="bill-col">
          <div><span className="bill-label">Control Number:</span><span className="bill-value control">{controlNo}</span></div>
          <div><span className="bill-label">Amount(TZS):</span><span className="bill-value amount">{formatMoney(bill.amount)}</span></div>
        </div>
        <button className="bill-toggle" onClick={handleToggle} aria-expanded={open}>
          {open ? "Hide details" : "Show details"}
        </button>
      </div>

      {open && (
        <div className="bill-details-container">
          {(bill.debt_remarks || bill.remarks) && (
            <div className="bill-remarks-box">
              {bill.debt_remarks && <p style={{ margin: "0 0 6px" }}><strong>Debt Remarks:</strong> {bill.debt_remarks}</p>}
              {bill.remarks && <p style={{ margin: "0" }}><strong>Enforcement Remarks:</strong> {bill.remarks}</p>}
            </div>
          )}

          {loading && (
            <div className="bill-loading-state">
              <span className="btn-spinner" style={{ display: "inline-block", width: "16px", height: "16px", border: "2px solid #1C236D", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.8s linear infinite", marginRight: "8px", verticalAlign: "middle" }}></span>
              Loading scan details...
            </div>
          )}

          {!loading && error && (
            <div className="bill-error-state">
              <span>{error}</span>
              <button onClick={loadScans} className="bill-retry-btn">Retry</button>
            </div>
          )}

          {!loading && !error && scans && (
            scans.length > 0 ? (
              channelId === "2" ? (
                <Channel2ScanTable scans={scans} />
              ) : (
                <Channel1ScanTable scans={scans} />
              )
            ) : (
              <div className="bill-empty-state">
                No scan records found for this bill.
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
}

// Single section: the current bill (first item) followed by any previous bills;
// each card shows only the control number and amount, with expandable details.
function ParkingBills({ bills, searchQuery }) {
  if (!bills?.length) return null;
  const [current, ...previous] = bills;
  return (
    <div className="parking-section">
      <h4 className="parking-title">TAMISEMI Parking Pendings</h4>
      <BillCard bill={current} searchQuery={searchQuery} />
      {previous.map((b, idx) => (
        <BillCard key={b.control_number || b.controlNumber || idx} bill={b} isPrevious searchQuery={searchQuery} />
      ))}
    </div>
  );
}

function Results({ show, data, onClose, searchType, searchQuery, checkMode, defaultTab }) {
  const [activeTab, setActiveTab] = useState("pending");

  // Reset to the relevant default tab and bring results into view whenever they appear
  useEffect(() => {
    if (show) {
      setActiveTab(defaultTab || "pending");
      document.getElementById("results")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [show, defaultTab]);

  if (!show || !data) return null;

  const isVehicle = searchType === "vehicle";
  const showPendingTab = checkMode === "offence";
  const showInspectionTab = checkMode === "inspection" && isVehicle;

  const formatStatus = (status) => {
    const s = (status || "").toUpperCase();
    return (
      <span className={`status-badge status-${s.toLowerCase()}`}>{s}</span>
    );
  };

  return (
    <section className="results-section" id="results">
      <div className="results-card">
        <button className="close-btn" onClick={onClose} title="Close results">✕</button>

        <div className="modal-header">
          <h3>
            <span className="modal-query-type">{searchType.toUpperCase()}</span>{" "}
            <span className="modal-query-value">{searchQuery}</span>
          </h3>
          {checkMode === "offence" && (
            <div className="offence-heading">
              <h4 className="parking-title">TPF Pending Offence(s)</h4>
              {data.totalPendingAmount && (
                <span className="offence-total">
                  Total Pending Amount:{" "}
                  <strong>TZS {Number(data.totalPendingAmount).toLocaleString()}</strong>
                </span>
              )}
            </div>
          )}
        </div>

        {/* ── Tabs: shown only when both are relevant (kept for future flexibility) ── */}
        {showPendingTab && showInspectionTab && (
          <div className="tab-buttons">
            <button
              className={`tab-button ${activeTab === "pending" ? "active" : ""}`}
              onClick={() => setActiveTab("pending")}
            >
              Pending Offences
              {data.pending_transactions?.length > 0 && (
                <span className="tab-badge">{data.pending_transactions.length}</span>
              )}
            </button>
            <button
              className={`tab-button ${activeTab === "inspection" ? "active" : ""}`}
              onClick={() => setActiveTab("inspection")}
            >
              Inspection Status
              {data.inspection_data?.length > 0 && (
                <span className="tab-badge">{data.inspection_data.length}</span>
              )}
            </button>
          </div>
        )}

        {/* ── Pending Offences Tab ── */}
        {showPendingTab && activeTab === "pending" && (
          data.pending_transactions?.length > 0 ? (
            <OffenceTable items={data.pending_transactions} searchType={searchType} />
          ) : (
            <div className="no-results">
              <p>No pending offences found.</p>
            </div>
          )
        )}

        {/* ── TAMISEMI Parking Pendings (vehicle searches only; see SHOW_TAMISEMI_PARKING) ── */}
        {SHOW_TAMISEMI_PARKING && showPendingTab && isVehicle && (
          <ParkingBills bills={data.parking_bills} searchQuery={searchQuery} />
        )}

        {/* ── Inspection Status Tab ── */}
        {showInspectionTab && activeTab === "inspection" && (
          data.inspection_data?.length > 0 ? (
            <div className="table-wrapper">
              <table className="transaction-table">
                <thead>
                  <tr>
                    <th>Inspection Date</th>
                    <th>Valid Until</th>
                    <th>Reason</th>
                    <th>Restriction on Use</th>
                    <th>Intended Use</th>
                    <th>Region</th>
                    <th>Final Result</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.inspection_data.map((item, idx) => {
                    // #4: all reasons expire except "Accident" (one-off
                    // event, not a periodic certification with a
                    // validity window)
                    const hasValidity = item.reason_en !== "Accident";
                    const validUntilDate = item.valid_untill ? new Date(item.valid_untill) : null;
                    const validUntilOk =
                      validUntilDate &&
                      !isNaN(validUntilDate.getTime()) &&
                      validUntilDate.getFullYear() >= 1971;
                    const isValid = hasValidity && validUntilOk && validUntilDate >= new Date();

                    return (
                      <tr key={idx}>
                        <td>{formatDate(item.inspection_date)}</td>
                        <td>{hasValidity ? formatDate(item.valid_untill) : "—"}</td>
                        <td>{item.reason_en ?? "—"}</td>
                        <td>{item.prohibition_on_use ?? "—"}</td>
                        <td>{item.vehicle_passed_for ?? "—"}</td>
                        <td>{item.region ?? "—"}</td>
                        <td>{formatStatus(item.finalresult)}</td>
                        <td>
                          {hasValidity && validUntilOk ? (
                            isValid ? (
                              <span className="status-badge status-pass">VALID</span>
                            ) : (
                              <span className="status-badge status-fail">EXPIRED</span>
                            )
                          ) : (
                            <span className="status-neutral">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="no-results">
              <span className="no-results-icon">🔍</span>
              <p>No inspection records found for this vehicle.</p>
            </div>
          )
        )}
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────
// Main Body Component
// ─────────────────────────────────────────────
function Body({ onResultsChange }) {
  const [checkMode, setCheckMode] = useState("offence"); // "offence" | "inspection"
  const [searchQuery, setSearchQuery] = useState("");
  const [modalData, setModalData] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  // what the displayed results were searched with (independent of the live input)
  const [submitted, setSubmitted] = useState({ type: "vehicle", query: "" });
  const [searchType, setSearchType] = useState("vehicle");
  const [loading, setLoading] = useState(false);

  // let the page hide the footer while results are on screen
  useEffect(() => {
    onResultsChange?.(modalVisible);
  }, [modalVisible, onResultsChange]);

  const allSearchOptions = [
    {
      icon: "fa-car",
      text: "Search By Registration",
      type: "vehicle",
      placeholder: "e.g. T359DTT",
    },
    {
      icon: "fa-id-card",
      text: "Search By License",
      type: "license",
      placeholder: "e.g. 4000453134",
    },
    {
      icon: "fa-receipt",
      text: "Search By Reference",
      type: "reference",
      placeholder: "e.g. 9910838966983",
    },
  ];

  // #1: keep all three buttons always visible (same navbar shape);
  // disable License/Reference in Inspection mode instead of hiding them
  const searchOptions = allSearchOptions;
  const isOptionDisabled = (type) =>
    checkMode === "inspection" && type !== "vehicle";

  const handleModeChange = (mode) => {
    setCheckMode(mode);
    setSearchQuery("");
    setModalVisible(false);
    if (mode === "inspection") {
      setSearchType("vehicle");
    }
  };

  // Clear search query when switching search type
  const handleTypeChange = (type) => {
    setSearchType(type);
    setSearchQuery("");
    setModalVisible(false);
  };

  const validateSearch = () => {
    const q = searchQuery.trim();
    if (searchType === "vehicle") {
      const standardPlate = /^[A-Za-z]\d{3}[A-Za-z]{3}$/;
      const motorcyclePlate = /^MC\d{3}[A-Za-z]{3}$/i;

      if (checkMode === "offence") {
        // Motorcycles don't accrue offence "deni" — only standard plates allowed here
        if (!standardPlate.test(q)) {
          Swal.fire({
            icon: "error",
            title: "Invalid Vehicle Registration",
            text: "Must be a standard plate. e.g. T849EDY",
          });
          return false;
        }
      } else {
        // Inspection mode: standard plates or motorcycle plates
        if (!standardPlate.test(q) && !motorcyclePlate.test(q)) {
          Swal.fire({
            icon: "error",
            title: "Invalid Vehicle Registration",
            text: "Use a standard plate (e.g. T849EDY).",
          });
          return false;
        }
      }
    }
    if (searchType === "license" && !/^\d{10,}$/.test(q)) {
      Swal.fire({
        icon: "error",
        title: "Invalid License Number",
        text: "Must be at least 10 digits. e.g. 4000453134",
      });
      return false;
    }
    if (searchType === "reference" && !/^\d{12,}$/.test(q)) {
      Swal.fire({
        icon: "error",
        title: "Invalid Reference Number",
        text: "Must be at least 12 digits. e.g. 9910838966983",
      });
      return false;
    }
    return true;
  };

  const handleSearch = async () => {
    const q = searchQuery.trim();
    if (!q) {
      Swal.fire({ icon: "error", title: "Input Required", text: "Please enter a search term." });
      return;
    }
    if (!validateSearch()) return;

    setLoading(true);
    setModalVisible(false);

    try {
      const endpoint = "/api/OffenceCheck";
      const response = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ [searchType]: q }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error: ${response.status}`);
      }

      const json = await response.json();

      let data;
      if (json.payload) {
        data = decryptPayload(json.payload);
      } else if (checkMode === "offence") {
        data = normalizeTmsResponse(json);
      } else {
        throw new Error("No payload in response");
      }

      // Query TAMISEMI Parking service for vehicle searches
      if (SHOW_TAMISEMI_PARKING && searchType === "vehicle") {
        try {
          const tamisemiData = await fetchDebtSummary(q);
          console.log("TAMISEMI Debt Summary:", tamisemiData);

          let bills = [];
          if (Array.isArray(tamisemiData?.data?.summary)) {
            bills = tamisemiData.data.summary;
          } else if (Array.isArray(tamisemiData?.summary)) {
            bills = tamisemiData.summary;
          } else if (Array.isArray(tamisemiData?.bills)) {
            bills = tamisemiData.bills;
          } else if (Array.isArray(tamisemiData?.data)) {
            bills = tamisemiData.data;
          }

          if (bills.length > 0) {
            data.parking_bills = bills.map((item) => ({
              control_number: item.controlNumber || item.control_number,
              amount: item.amount,
              enforceable_amount: item.enforceableAmount,
              debt_remarks: item.debtRemarks,
              remarks: item.remarks,
              channel_id: item.channelId,
              bill_id: item.billId,
              plate_number: item.plateNumber,
              transactions: item.transactions || [],
            }));
          }
        } catch (tErr) {
          console.warn("TAMISEMI debt-summary check failed:", tErr);
        }
      }

      setLoading(false);

      if (data?.status !== "success") {
        Swal.fire({
          icon: "error",
          title: "No Results",
          text: "No results found for the provided search term.",
        });
        return;
      }

      if (checkMode === "inspection") {
        const hasInspection = data.inspection_data?.length > 0;

        if (!hasInspection) {
          Swal.fire({
            icon: "info",
            title: "Hakuna Taarifa za Ukaguzi",
            text: `Gari lenye namba ${q} halina taarifa yoyote ya ukaguzi (inspection).`,
          });
          return;
        }

        setModalData(data);
        setSubmitted({ type: searchType, query: q });
        setModalVisible(true);
        return;
      }

      // Offence mode: check both pending traffic offences and parking bills
      const hasPending =
        (data.pending_transactions?.length > 0) ||
        (SHOW_TAMISEMI_PARKING && data.parking_bills?.length > 0);

      if (!hasPending) {
        let message = "";

        if (searchType === "vehicle") {
          message = `Gari lenye namba ${q} halidaiwi.`;
        }
        if (searchType === "license") {
          message = `Leseni namba ${q} haidaiwi.`;
        }
        if (searchType === "reference") {
          message = `Reference namba ${q} haidaiwi.`;
        }

        Swal.fire({
          icon: "success",
          title: "Hakuna Deni",
          text: message,
        });
        return;
      }

      setModalData(data);
      setSubmitted({ type: searchType, query: q });
      setModalVisible(true);
    } catch (error) {
      console.error("Search error:", error);
      setLoading(false);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to fetch data. Please check your connection and try again.",
      });
    }
  };

  // Allow pressing Enter to search
  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSearch();
  };

  const currentOption = searchOptions.find((o) => o.type === searchType) || searchOptions[0];
  const activePlaceholder =
    currentOption.type === "vehicle"
      ? checkMode === "offence"
        ? "e.g. T359DTT"
        : "e.g. T359DTT"
      : currentOption.placeholder;

  return (
    <>
    <main className="body-container">
      <div className="content-container">
        {/* ── Mode Toggle: Check Offence / Check Inspection ── */}
        <div className="mode-toggle">
          <button
            className={`mode-button ${checkMode === "offence" ? "active" : ""}`}
            onClick={() => handleModeChange("offence")}
          >
            <i className="fa fa-exclamation-triangle"></i> Check Offence
          </button>
          <button
            className={`mode-button ${checkMode === "inspection" ? "active" : ""}`}
            onClick={() => handleModeChange("inspection")}
          >
            <i className="fa fa-clipboard-check"></i> Check Inspection
          </button>
        </div>

        {/* ── Search Type Tabs ── */}
        <div className="navbar">
          {searchOptions.map((opt, idx) => {
            const disabled = isOptionDisabled(opt.type);
            return (
              <React.Fragment key={idx}>
                <button
                  className={`nav-button ${searchType === opt.type ? "active" : ""} ${disabled ? "disabled" : ""}`}
                  onClick={() => !disabled && handleTypeChange(opt.type)}
                  disabled={disabled}
                  title={disabled ? "Not available in Check Inspection mode" : ""}
                >
                  <i className={`fa ${opt.icon}`}></i> {opt.text}
                </button>
                {idx < searchOptions.length - 1 && (
                  <span className="separator">|</span>
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* ── Search Input ── */}
        <div className="search-box">
          <div className="search-input-wrapper">
            <i className={`fa ${currentOption.icon} search-icon`}></i>
            <input
              type="text"
              placeholder={activePlaceholder}
              className="search-input"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                if (!e.target.value) setModalVisible(false);
              }}
              onKeyDown={handleKeyDown}
              autoComplete="off"
              spellCheck="false"
            />
            {searchQuery && (
              <button
                className="clear-btn"
                onClick={() => { setSearchQuery(""); setModalVisible(false); }}
                title="Clear"
              >
                ✕
              </button>
            )}
          </div>
          <button
            className="search-button"
            onClick={handleSearch}
            disabled={loading}
          >
            {loading ? (
              <span className="btn-spinner" />
            ) : (
              <>
                <i className="fa fa-search"></i> Search
              </>
            )}
          </button>
        </div>
      </div>

      {/* ── Loading Bar ── */}
      {loading && (
        <div className="loader">
          <div className="loader-bar"></div>
          <div className="loader-bar"></div>
          <div className="loader-bar"></div>
        </div>
      )}

    </main>

    {/* ── Results (inline, below the search) ── */}
    <Results
      show={modalVisible}
      data={modalData}
      onClose={() => setModalVisible(false)}
      searchType={submitted.type}
      searchQuery={submitted.query}
      checkMode={checkMode}
      defaultTab={checkMode === "inspection" ? "inspection" : "pending"}
    />
    </>
  );
}

export default Body;

